import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useApp } from "@/context/AppContext";
import { daysUntil, formatDeadline, formatMoney } from "@/lib/mockData";
import { AlertTriangle, CalendarDays, ArrowUpRight, FileSignature, Wand2, Send, CheckCircle2, Clock, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Rich applications + deadlines panel.
 *
 * Groups the user's open applications into urgency buckets using the deadline
 * parsed from the application itself OR the underlying catalog target. Each
 * row carries the app's progress, suggested next action (prep → sign → file),
 * and a jump link.
 */
const TABS = [
    { id: "overdue", label: "Overdue", color: "destructive" },
    { id: "week",    label: "This week", color: "terracotta" },
    { id: "month",   label: "This month", color: "primary" },
    { id: "later",   label: "Later", color: "muted" },
    { id: "done",    label: "Filed & done", color: "success" },
];

export default function ApplicationsDeadlinesPanel() {
    const { applications, scoredGrants } = useApp();
    const [tab, setTab] = useState(null); // lazily resolved to first non-empty

    const enriched = useMemo(() => {
        const gById = new Map(scoredGrants.map(g => [g.id, g]));
        return (applications || []).map(a => {
            const g = gById.get(a.target_id);
            const deadlineRaw = a.deadline || a.target_meta?.close_date || a.target_meta?.deadline || g?.deadline;
            const d = daysUntil(deadlineRaw);
            return { ...a, _deadline: deadlineRaw, _days: d, _grant: g };
        });
    }, [applications, scoredGrants]);

    const buckets = useMemo(() => {
        const b = { overdue: [], week: [], month: [], later: [], done: [] };
        for (const a of enriched) {
            if (["approved", "denied"].includes(a.status) || a.confirmation_number) {
                b.done.push(a);
                continue;
            }
            const d = a._days;
            if (d !== null && d < 0) b.overdue.push(a);
            else if (d !== null && d <= 7) b.week.push(a);
            else if (d !== null && d <= 30) b.month.push(a);
            else b.later.push(a);
        }
        // Sort urgency groups ascending by days; later/done reverse-chronological-ish.
        for (const k of ["overdue", "week", "month"]) {
            b[k].sort((x, y) => (x._days ?? 999) - (y._days ?? 999));
        }
        b.later.sort((x, y) => (x._days ?? 9999) - (y._days ?? 9999));
        b.done.sort((x, y) => (y.submitted_at || "").localeCompare(x.submitted_at || ""));
        return b;
    }, [enriched]);

    const firstNonEmpty = TABS.find(t => buckets[t.id].length > 0)?.id || "later";
    const activeTab = tab || firstNonEmpty;
    const list = buckets[activeTab];

    const totalOpen = buckets.overdue.length + buckets.week.length + buckets.month.length + buckets.later.length;

    if ((applications || []).length === 0) {
        return (
            <Card className="p-8 border-dashed border-border text-center" data-testid="apps-panel-empty">
                <FileSignature className="h-6 w-6 text-muted-foreground mx-auto" />
                <h3 className="mt-3 font-serif text-2xl">No applications yet</h3>
                <p className="mt-2 text-sm text-muted-foreground max-w-sm mx-auto">Start one from Grant matches and Owed AI will prepare the packet. Deadlines will appear here once you do.</p>
                <Button asChild className="mt-5 rounded-full bg-foreground text-background hover:bg-foreground/90">
                    <Link to="/app/grants">Browse grants <ArrowRight className="h-4 w-4 ml-1" /></Link>
                </Button>
            </Card>
        );
    }

    return (
        <Card className="p-6 sm:p-7 border-border" data-testid="apps-deadlines-panel">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Your applications</p>
                    <h3 className="mt-1 font-serif text-2xl sm:text-3xl leading-tight">
                        {totalOpen > 0 ? `${totalOpen} open — grouped by urgency` : "All caught up"}
                    </h3>
                </div>
                <Button asChild variant="ghost" size="sm" className="rounded-full">
                    <Link to="/app/applications" data-testid="apps-panel-open-tracker">Open tracker <ArrowUpRight className="h-4 w-4 ml-1" /></Link>
                </Button>
            </div>

            <div className="mt-5 flex gap-2 overflow-x-auto pb-1" role="tablist">
                {TABS.map(t => {
                    const count = buckets[t.id].length;
                    const on = activeTab === t.id;
                    return (
                        <button
                            key={t.id}
                            role="tab"
                            onClick={() => setTab(t.id)}
                            className={cn(
                                "shrink-0 inline-flex items-center gap-2 rounded-full px-4 h-9 text-xs font-medium transition-colors border",
                                on
                                    ? "bg-foreground text-background border-foreground"
                                    : "bg-card text-muted-foreground border-border hover:text-foreground"
                            )}
                            data-testid={`apps-tab-${t.id}`}
                        >
                            <span className={cn(
                                "h-1.5 w-1.5 rounded-full",
                                t.color === "destructive" && "bg-destructive",
                                t.color === "terracotta" && "bg-terracotta",
                                t.color === "primary" && "bg-primary",
                                t.color === "success" && "bg-success",
                                t.color === "muted" && "bg-muted-foreground",
                            )} />
                            {t.label}
                            <Badge
                                variant="outline"
                                className={cn(
                                    "border-none h-5 min-w-5 px-1.5 text-[10px] font-mono",
                                    on ? "bg-background/15 text-background" : "bg-secondary text-muted-foreground"
                                )}
                            >
                                {count}
                            </Badge>
                        </button>
                    );
                })}
            </div>

            <div className="mt-5 divide-y divide-border" data-testid={`apps-list-${activeTab}`}>
                {list.length === 0 ? (
                    <p className="py-8 text-center text-sm text-muted-foreground">Nothing here. Try another tab.</p>
                ) : list.map((a, i) => (
                    <AppRow key={a.id || i} app={a} bucket={activeTab} />
                ))}
            </div>
        </Card>
    );
}

function AppRow({ app, bucket }) {
    const d = app._days;
    const urgencyChip = bucket === "overdue"
        ? <Badge variant="outline" className="bg-destructive/10 border-destructive/40 text-destructive text-[10px]"><AlertTriangle className="h-3 w-3 mr-1" /> {d !== null ? `${Math.abs(d)}d overdue` : "overdue"}</Badge>
        : bucket === "week"
            ? <Badge variant="outline" className="bg-terracotta/10 border-terracotta/40 text-terracotta text-[10px]"><Clock className="h-3 w-3 mr-1" /> {d === 0 ? "due today" : `${d}d left`}</Badge>
            : bucket === "month"
                ? <Badge variant="outline" className="bg-secondary border-border text-[10px]"><CalendarDays className="h-3 w-3 mr-1" /> {d}d left</Badge>
                : bucket === "done"
                    ? <Badge variant="outline" className="bg-success/10 border-success/40 text-success text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1" /> {app.status === "approved" ? "Approved" : "Filed"}</Badge>
                    : <Badge variant="outline" className="bg-secondary border-border text-[10px]"><CalendarDays className="h-3 w-3 mr-1" /> {d !== null ? `${d}d` : "rolling"}</Badge>;

    const action = nextAction(app);

    return (
        <div className="py-4 grid grid-cols-12 gap-3 items-center" data-testid={`apps-row-${app.id}`}>
            <div className="col-span-12 sm:col-span-6 min-w-0">
                <div className="flex items-center gap-2">
                    {urgencyChip}
                    <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{app.type}</p>
                </div>
                <p className="mt-1.5 font-serif text-base leading-tight line-clamp-2">{app.target_title}</p>
                {app._deadline && <p className="mt-1 text-[11px] text-muted-foreground">Deadline · {formatDeadline(app._deadline)}</p>}
            </div>
            <div className="col-span-7 sm:col-span-3">
                <div className="flex items-center justify-between text-[10px] text-muted-foreground font-mono uppercase tracking-wider">
                    <span>{app.status?.replace("_", " ") || "draft"}</span>
                    <span>{app.progress ?? 0}%</span>
                </div>
                <Progress value={app.progress || 0} className="mt-1.5 h-1.5" />
                {app.target_amount ? (
                    <p className="mt-1.5 text-xs text-muted-foreground">Up to <span className="font-serif text-foreground">{formatMoney(app.target_amount, { compact: true })}</span></p>
                ) : null}
            </div>
            <div className="col-span-5 sm:col-span-3 flex justify-end">
                <Button asChild size="sm" variant={action.variant} className={cn("rounded-full h-9 w-full sm:w-auto", action.variant !== "outline" && "bg-foreground text-background hover:bg-foreground/90")}>
                    <Link to={`/app/detail/${app.type}/${app.target_id}`} data-testid={`apps-row-cta-${app.id}`}>
                        {action.icon} <span className="ml-1">{action.label}</span>
                    </Link>
                </Button>
            </div>
        </div>
    );
}

function nextAction(app) {
    if (app.status === "approved") return { label: "View", variant: "outline", icon: <ArrowUpRight className="h-3.5 w-3.5" /> };
    if (app.confirmation_number || app.status === "under_review") return { label: "Track", variant: "outline", icon: <ArrowUpRight className="h-3.5 w-3.5" /> };
    if (app.signed) return { label: "File", variant: "default", icon: <Send className="h-3.5 w-3.5" /> };
    if (app.prepared_at) return { label: "Sign", variant: "default", icon: <FileSignature className="h-3.5 w-3.5" /> };
    return { label: "AI prep", variant: "default", icon: <Wand2 className="h-3.5 w-3.5" /> };
}
